'use client';

import { cx } from '@/utils/cx';
import { Transition } from '@headlessui/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CheckIcon } from '@radix-ui/react-icons';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/utils/supabase/client';

export const Paywall = ({ children }: { children: React.ReactNode }) => {
  const [checkout, setCheckout] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const base = process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK as string;

    createClient()
      .auth.getUser()
      .then(({ data: { user } }) => {
        if (user?.email) {
          setCheckout(`${base}?prefill_email=${encodeURIComponent(user.email)}`);
        } else {
          setCheckout(base);
        }
      })
      .catch(() => setCheckout(base));
  }, []);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitive.Trigger asChild>
        <div>{children}</div>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal forceMount>
        <Transition.Root show={isOpen}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogPrimitive.Overlay
              forceMount
              className="fixed inset-0 z-20 bg-black/50"
            />
          </Transition.Child>

          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPrimitive.Content
              forceMount
              className={cx(
                'fixed z-50',
                'w-[95vw] max-w-md rounded-2xl p-8 md:w-full',
                'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%]',
                'bg-zinc-800',
                'focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75'
              )}
            >
              <DialogPrimitive.Title className="sr-only">
                Upgrade to Premium
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Premium features and benefits
              </DialogPrimitive.Description>

              <div className="text-2xl font-bold uppercase italic text-wave-brown">
                TAKE YOUR MUSIC & AUDIO TO THE NEXT LEVEL WITH 1 CLICK LYRIC
                VISUALIZATION!
              </div>

              <div className="h-6" />

              <ul className="space-y-3">
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Access our extended template library.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Access new templates as they are released.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Remove the Beatwave watermark.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Upload video backgrounds.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Automated lyric visualization in 1 click.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  Your own asset library.
                </li>
                <li className="flex flex-row gap-2">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  And more.
                </li>
              </ul>

              <div className="h-10" />

              <div className="text-base font-bold italic text-wave-brown">
                For a limited time, you can unlock all of this and more for as
                little as $8.25 a month. Are you ready to take your music and
                audio to the next level with video?
              </div>

              <div className="h-5" />

              <Link
                href={checkout}
                className={cx(
                  'my-1 block rounded-full bg-wave-green px-5 py-2 text-center text-lg text-slate-800',
                  'mb-5 h-11 w-full font-bold uppercase italic'
                )}
              >
                Upgrade instantly
              </Link>

              <a
                href={`https://beatwaveapp.com/plus-upgrade/?utm_source=app&utm_medium=banner&utm_campaign=upgrade-popup`}
                target={'_blank'}
                rel={'noreferrer'}
                className={cx(
                  'my-1 block rounded-full bg-slate-200 px-5 py-2 text-center text-lg text-slate-800',
                  'font-bold uppercase italic'
                )}
              >
                Learn more
              </a>
            </DialogPrimitive.Content>
          </Transition.Child>
        </Transition.Root>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};